import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable "Upload image" button. Sends a PNG/JPG/WebP/GIF to
// POST /api/uploads/image and calls onUploaded(publicUrl) with the resulting URL.
const ImageUploadButton = ({ onUploaded, testid = "image-upload", label = "Upload" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so the same file can be picked again later
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image exceeds 10 MB limit");
      return;
    }

    setBusy(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const fullUrl = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      onUploaded(fullUrl, data);
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.gif,image/*"
        onChange={handleFile}
        data-testid={`${testid}-input`}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        data-testid={testid}
        disabled={busy}
        className="flex items-center gap-2 border border-white/30 px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#FAFAFA] transition-colors hover:bg-white hover:text-black disabled:opacity-60"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            {progress || 0}%
          </>
        ) : (
          <>
            <Upload size={14} /> {label}
          </>
        )}
      </button>
    </>
  );
};

export default ImageUploadButton;
